import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { SessionService } from '@galaxy/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService, PersonaType } from '@vendasta/iam';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from '../../globals';

@Component({
  selector: 'app-local-login',
  styleUrls: ['./local-login.component.scss'],
  templateUrl: './local-login.component.html',
  standalone: false,
})
export class LocalLoginComponent implements OnInit {
  email: string;
  password: string;
  partnerId: string;
  sessionToken: string;
  loading: boolean;

  environment = environment;

  constructor(
    private iamService: IAMService,
    private snackbarService: SnackbarService,
    private sessionService: SessionService,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(take(1)).subscribe((paramMap: ParamMap) => {
      if (paramMap.get('session_id')) {
        this.signIn(paramMap.get('session_id'));
      }

      if (paramMap.get('error')) {
        const errorStatus = paramMap.get('status');
        this.handleError(errorStatus);

        this.router.navigate(['/login'], {});
      }
    });
  }

  handleError(statusCode: number | string): void {
    if (statusCode === '404' || statusCode === 404) {
      this.snackbarService.openErrorSnack('User not found.');
    } else if (statusCode === '401' || statusCode === 401) {
      this.snackbarService.openErrorSnack('Incorrect email/password.');
    } else if (statusCode === '403' || statusCode === 403) {
      this.snackbarService.openErrorSnack("Superadmins can't use email/password. Please sign in with google.");
    } else {
      this.snackbarService.openErrorSnack('An error occured');
    }
  }

  private signIn(sessionId: string): void {
    this.sessionService.setSessionId(sessionId);
    this.loading = true;
    this.createSession(sessionId).subscribe(
      () => {
        this.loading = false;
        this.redirectToNextUrl();
      },
      (err) => {
        this.snackbarService.openErrorSnack(`Invalid login: ${err.message}`);
        this.loading = false;
      },
    );
  }

  private redirectToNextUrl(): void {
    this.route.queryParamMap.pipe(take(1)).subscribe((paramMap: ParamMap) => {
      const nextUrl = paramMap.get('next_url');
      if (nextUrl) {
        window.location.href = nextUrl;
      } else {
        window.location.href = '/';
      }
    });
  }

  login(form: NgForm): void {
    if (this.sessionToken) {
      this.signIn(this.sessionToken);
      return;
    }
    if (!form.valid) {
      return;
    }

    this.iamService
      .login(this.email, this.password, PersonaType.partner)
      .pipe(
        take(1),
        catchError((e: HttpErrorResponse) => {
          this.handleError(e.status);
          return of('');
        }),
      )
      .subscribe((sessionId: string) => {
        if (sessionId) {
          this.signIn(sessionId);
        }
      });
  }

  loginWithGoogle(): void {
    this.iamService.ssoLogin(window.location.href);
  }

  createSession(iamSession: string): Observable<boolean> {
    let params = `sessionId=${iamSession}`;

    if (this.partnerId) {
      this.partnerId = this.partnerId.toUpperCase();
      params += `&partnerId=${this.partnerId}`;
    }

    return this.http
      .post('/_ajax/v1/create-session', params, {
        headers: new HttpHeaders({
          'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
        }),
      })
      .pipe(map(() => true));
  }
}
